import React from "react";
import {
  Modal,
  Slide,
  Box,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { ReactComponent as CloseIcon } from "../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { TOKENS } from "./tokens";

/**
 * Right-anchored read-only detail drawer.
 *
 * Chrome matches the WAF Exception side-drawer reference
 * (src/components/pages/_new/services/web_application/components/modal/ConfigWAFExceptionSettings.js):
 *   - MUI Modal + Slide direction="left" (NOT MUI Drawer)
 *   - 33% width, minWidth 480
 *   - rgba(0,0,0,0.5) backdrop, -4px 0 20px shadow, top-left rounded only
 *   - Optional 48x48 indigo icon box on the left of the header
 *   - Optional subtitle line under the title
 *   - ReactPerfectScrollbar body
 *
 * `headerChips` is an array of ReactNodes (StatusPill / PlanChip / etc).
 * `footer` is an optional ReactNode rendered in a sticky footer bar.
 */
const DetailDrawer = ({
  open,
  onClose,
  title,
  subtitle,
  icon,
  headerChips,
  footer,
  children,
}) => (
  <Modal
    open={!!open}
    onClose={onClose}
    slotProps={{
      backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" } },
    }}
  >
    <Slide direction="left" in={!!open} mountOnEnter unmountOnExit>
      <Stack
        sx={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "33%",
          minWidth: 480,
          height: "100vh",
          backgroundColor: "#fff",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
          zIndex: 1300,
          borderTopLeftRadius: "8px",
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          spacing={2}
          sx={{ px: 4, pt: 4, pb: 3, flexShrink: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
            {icon ? (
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#6371F7",
                    "& > svg": { width: 32, height: 32, color: "#6371F7" },
                  }}
                >
                  {icon}
                </Box>
              </Box>
            ) : null}
            <Stack sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: "medium",
                  fontSize: 20,
                  lineHeight: "30px",
                  color: TOKENS.fg1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {title}
              </Typography>
              {subtitle ? (
                <Typography
                  sx={{
                    fontSize: 12,
                    lineHeight: "18px",
                    color: TOKENS.fg2,
                  }}
                >
                  {subtitle}
                </Typography>
              ) : null}
              {Array.isArray(headerChips) && headerChips.length > 0 ? (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                  {headerChips.map((chip, i) => (
                    <React.Fragment key={i}>{chip}</React.Fragment>
                  ))}
                </Box>
              ) : null}
            </Stack>
          </Stack>
          <IconButton onClick={onClose} aria-label="Close" sx={{ p: 0, color: TOKENS.fg2 }}>
            <CloseIcon style={{ color: TOKENS.fg2 }} />
          </IconButton>
        </Stack>

        {/* Body — scrollable */}
        <Box sx={{ flex: 1, minHeight: 0, py: 1, pl: 4 }}>
          <ReactPerfectScrollbar>
            <Box sx={{ pr: 4, pb: 2 }}>{children}</Box>
          </ReactPerfectScrollbar>
        </Box>

        {footer ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              px: 4,
              py: 2,
              borderTop: `1px solid ${TOKENS.border}`,
              background: TOKENS.bgSurface,
              flexShrink: 0,
            }}
          >
            {footer}
          </Box>
        ) : null}
      </Stack>
    </Slide>
  </Modal>
);

export default DetailDrawer;
